import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ROLE_LABEL = { patient: "Paziente", professional: "Professionista", admin: "Admin" };

export default function GestioneUtenti() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const users = data?.users || [];

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/users/${id}/status?status=${status}`); toast.success("Stato aggiornato."); qc.invalidateQueries({ queryKey: ["admin-users"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const render = (list) => (
    <div className="overflow-x-auto bg-white rounded-2xl border border-[#D2E3DB]">
      <Table>
        <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead><TableHead>Stato</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
        <TableBody>
          {list.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-semibold text-[#102A2A]">{u.first_name} {u.last_name}</TableCell>
              <TableCell className="text-[#5C6B68]">{u.email}</TableCell>
              <TableCell>{ROLE_LABEL[u.role]}</TableCell>
              <TableCell><StatusBadge status={u.status === "active" ? "verificato" : "annullata"} label={u.status === "active" ? "Attivo" : "Disattivato"} /></TableCell>
              <TableCell>
                {u.role !== "admin" && (u.status === "active" ? (
                  <Button data-testid={`disable-${u.id}`} variant="outline" onClick={() => setStatus(u.id, "disabled")} className="rounded-lg h-9 px-3 text-sm border-2 border-[#C0392B]/30 text-[#C0392B]">Disattiva</Button>
                ) : (
                  <Button data-testid={`enable-${u.id}`} variant="outline" onClick={() => setStatus(u.id, "active")} className="rounded-lg h-9 px-3 text-sm border-2 border-[#087A73] text-[#087A73]">Riattiva</Button>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <DashboardLayout title="Gestione utenti">
      {isLoading ? <LoadingBlock /> : (
        <Tabs defaultValue="all">
          <TabsList className="bg-white border border-[#D2E3DB] rounded-xl mb-4">
            <TabsTrigger value="all" data-testid="tab-all">Tutti</TabsTrigger>
            <TabsTrigger value="patient" data-testid="tab-patients">Pazienti</TabsTrigger>
            <TabsTrigger value="professional" data-testid="tab-pros">Professionisti</TabsTrigger>
          </TabsList>
          <TabsContent value="all">{render(users)}</TabsContent>
          <TabsContent value="patient">{render(users.filter((u) => u.role === "patient"))}</TabsContent>
          <TabsContent value="professional">{render(users.filter((u) => u.role === "professional"))}</TabsContent>
        </Tabs>
      )}
    </DashboardLayout>
  );
}
